import {
  createHeaderSignature,
  normalizeHeaderCell,
  parseMarkdownTable,
} from "./markdownBlockParser";
import {
  NormalizedTableBlock,
  NormalizedTableCell,
  TableMatch,
  TableObject,
} from "./markdownDiffTypes";

const IDENTIFIER_HEADER_EXACT_MATCHES = new Set(["id", "uid", "key"]);

type CandidatePair = {
  previousIndex: number;
  currentIndex: number;
};

type IdentifierColumn = {
  index: number;
  normalizedHeader: string;
};

export const compareTables = (
  previousContentTables: TableObject[],
  currentContentTables: TableObject[]
): TableMatch[] => {
  const previousTables = previousContentTables.map(
    tableObjectToNormalizedTable
  );
  const currentTables = currentContentTables.map(tableObjectToNormalizedTable);
  const matchedPairs = matchNormalizedTables(previousTables, currentTables);
  const matchedCurrent = new Set(matchedPairs.values());

  const results: TableMatch[] = previousTables.map((_, previousIndex) => ({
    previousContentTableIndex: previousIndex,
    currentContentTableIndex: matchedPairs.get(previousIndex) ?? null,
  }));

  currentTables.forEach((_, currentIndex) => {
    if (!matchedCurrent.has(currentIndex)) {
      results.push({
        previousContentTableIndex: null,
        currentContentTableIndex: currentIndex,
      });
    }
  });

  return results;
};

export const matchNormalizedTables = (
  previousTables: NormalizedTableBlock[],
  currentTables: NormalizedTableBlock[]
): Map<number, number> => {
  const matches = new Map<number, number>();
  const matchedPrevious = new Set<number>();
  const matchedCurrent = new Set<number>();

  applyPairs(
    getUniqueHeaderSignaturePairs(previousTables, currentTables),
    matches,
    matchedPrevious,
    matchedCurrent
  );

  applyPairs(
    getRepeatedHeaderSignaturePairs(
      previousTables,
      currentTables,
      matchedPrevious,
      matchedCurrent
    ),
    matches,
    matchedPrevious,
    matchedCurrent
  );

  applyPairs(
    getHeaderlessRowFragmentPairs(
      previousTables,
      currentTables,
      matchedPrevious,
      matchedCurrent
    ),
    matches,
    matchedPrevious,
    matchedCurrent
  );

  applyPairs(
    getSingleColumnSchemaEditPairs(
      previousTables,
      currentTables,
      matchedPrevious,
      matchedCurrent
    ),
    matches,
    matchedPrevious,
    matchedCurrent
  );

  applyPairs(
    getIdentifierRenamePairs(
      previousTables,
      currentTables,
      matchedPrevious,
      matchedCurrent
    ),
    matches,
    matchedPrevious,
    matchedCurrent
  );

  return matches;
};

export const isLikelyIdentifierHeader = (normalizedHeader: string): boolean => {
  if (IDENTIFIER_HEADER_EXACT_MATCHES.has(normalizedHeader)) {
    return true;
  }

  return normalizedHeader.endsWith(" id");
};

export const findSingleIdentifierColumn = (
  headerCells: string[]
): IdentifierColumn | null => {
  const candidates = headerCells
    .map((headerCell, index) => ({
      index,
      normalizedHeader: normalizeHeaderCell(headerCell),
    }))
    .filter(({ normalizedHeader }) =>
      isLikelyIdentifierHeader(normalizedHeader)
    );

  if (candidates.length !== 1) {
    return null;
  }

  return candidates[0];
};

export const createUniqueRowKeyMap = (
  rows: string[][],
  keyColumnIndex: number
): Map<string, { row: string[]; index: number }> | null => {
  const rowMap = new Map<string, { row: string[]; index: number }>();

  for (const [index, row] of rows.entries()) {
    const rowKey = normalizeRowKey(row[keyColumnIndex] || "");
    if (!rowKey || rowMap.has(rowKey)) {
      return null;
    }

    rowMap.set(rowKey, { row, index });
  }

  return rowMap;
};

const applyPairs = (
  pairs: CandidatePair[],
  matches: Map<number, number>,
  matchedPrevious: Set<number>,
  matchedCurrent: Set<number>
) => {
  pairs.forEach(({ previousIndex, currentIndex }) => {
    if (
      matchedPrevious.has(previousIndex) ||
      matchedCurrent.has(currentIndex)
    ) {
      return;
    }

    matches.set(previousIndex, currentIndex);
    matchedPrevious.add(previousIndex);
    matchedCurrent.add(currentIndex);
  });
};

const getUniqueHeaderSignaturePairs = (
  previousTables: NormalizedTableBlock[],
  currentTables: NormalizedTableBlock[]
): CandidatePair[] => {
  const previousBySignature = groupIndicesBySignature(previousTables);
  const currentBySignature = groupIndicesBySignature(currentTables);
  const pairs: CandidatePair[] = [];
  let lastCurrentIndex = -1;

  previousTables.forEach((table, previousIndex) => {
    const previousIndices = previousBySignature.get(table.headerSignature);
    const currentIndices = currentBySignature.get(table.headerSignature);
    if (previousIndices?.length !== 1 || currentIndices?.length !== 1) {
      return;
    }

    const currentIndex = currentIndices[0];
    if (currentIndex <= lastCurrentIndex) {
      return;
    }

    pairs.push({ previousIndex, currentIndex });
    lastCurrentIndex = currentIndex;
  });

  return pairs;
};

const getRepeatedHeaderSignaturePairs = (
  previousTables: NormalizedTableBlock[],
  currentTables: NormalizedTableBlock[],
  matchedPrevious: Set<number>,
  matchedCurrent: Set<number>
): CandidatePair[] => {
  const previousEntries = previousTables
    .map((table, previousIndex) => ({
      previousIndex,
      signature: table.headerSignature,
    }))
    .filter(({ previousIndex }) => !matchedPrevious.has(previousIndex));
  const currentEntries = currentTables
    .map((table, currentIndex) => ({
      currentIndex,
      signature: table.headerSignature,
    }))
    .filter(({ currentIndex }) => !matchedCurrent.has(currentIndex));

  return buildLcsPairs(
    previousEntries,
    currentEntries,
    (previousEntry, currentEntry) =>
      previousEntry.signature !== "" &&
      previousEntry.signature === currentEntry.signature,
    (previousEntry, currentEntry) => ({
      previousIndex: previousEntry.previousIndex,
      currentIndex: currentEntry.currentIndex,
    })
  );
};

const getHeaderlessRowFragmentPairs = (
  previousTables: NormalizedTableBlock[],
  currentTables: NormalizedTableBlock[],
  matchedPrevious: Set<number>,
  matchedCurrent: Set<number>
): CandidatePair[] => {
  const previousCandidates = new Map<number, number[]>();
  const currentCandidates = new Map<number, number[]>();

  previousTables.forEach((previousTable, previousIndex) => {
    if (matchedPrevious.has(previousIndex) || !previousTable.isHeaderless) {
      return;
    }

    currentTables.forEach((currentTable, currentIndex) => {
      if (matchedCurrent.has(currentIndex) || !currentTable.isHeaderless) {
        return;
      }

      if (!hasHeaderlessRowOverlap(previousTable, currentTable)) {
        return;
      }

      const previousMatches = previousCandidates.get(previousIndex) ?? [];
      previousMatches.push(currentIndex);
      previousCandidates.set(previousIndex, previousMatches);

      const currentMatches = currentCandidates.get(currentIndex) ?? [];
      currentMatches.push(previousIndex);
      currentCandidates.set(currentIndex, currentMatches);
    });
  });

  const pairs: CandidatePair[] = [];
  let lastCurrentIndex = -1;

  previousTables.forEach((_, previousIndex) => {
    const currentMatches = previousCandidates.get(previousIndex) ?? [];
    if (currentMatches.length !== 1) {
      return;
    }

    const currentIndex = currentMatches[0];
    if (currentIndex <= lastCurrentIndex) {
      return;
    }

    const reciprocalMatches = currentCandidates.get(currentIndex) ?? [];
    if (
      reciprocalMatches.length !== 1 ||
      reciprocalMatches[0] !== previousIndex
    ) {
      return;
    }

    pairs.push({ previousIndex, currentIndex });
    lastCurrentIndex = currentIndex;
  });

  return pairs;
};

const getSingleColumnSchemaEditPairs = (
  previousTables: NormalizedTableBlock[],
  currentTables: NormalizedTableBlock[],
  matchedPrevious: Set<number>,
  matchedCurrent: Set<number>
): CandidatePair[] => {
  const previousCandidates = new Map<number, number[]>();
  const currentCandidates = new Map<number, number[]>();

  previousTables.forEach((previousTable, previousIndex) => {
    if (matchedPrevious.has(previousIndex)) {
      return;
    }

    currentTables.forEach((currentTable, currentIndex) => {
      if (matchedCurrent.has(currentIndex)) {
        return;
      }

      if (!isSingleNonIdentifierColumnEdit(previousTable, currentTable)) {
        return;
      }

      const previousMatches = previousCandidates.get(previousIndex) ?? [];
      previousMatches.push(currentIndex);
      previousCandidates.set(previousIndex, previousMatches);

      const currentMatches = currentCandidates.get(currentIndex) ?? [];
      currentMatches.push(previousIndex);
      currentCandidates.set(currentIndex, currentMatches);
    });
  });

  const pairs: CandidatePair[] = [];
  let lastCurrentIndex = -1;

  previousTables.forEach((_, previousIndex) => {
    const currentMatches = previousCandidates.get(previousIndex) ?? [];
    if (currentMatches.length !== 1) {
      return;
    }

    const currentIndex = currentMatches[0];
    if (currentIndex <= lastCurrentIndex) {
      return;
    }

    const reciprocalMatches = currentCandidates.get(currentIndex) ?? [];
    if (
      reciprocalMatches.length !== 1 ||
      reciprocalMatches[0] !== previousIndex
    ) {
      return;
    }

    pairs.push({ previousIndex, currentIndex });
    lastCurrentIndex = currentIndex;
  });

  return pairs;
};

const getIdentifierRenamePairs = (
  previousTables: NormalizedTableBlock[],
  currentTables: NormalizedTableBlock[],
  matchedPrevious: Set<number>,
  matchedCurrent: Set<number>
): CandidatePair[] => {
  const previousCandidates = new Map<number, number[]>();
  const currentCandidates = new Map<number, number[]>();

  previousTables.forEach((previousTable, previousIndex) => {
    if (matchedPrevious.has(previousIndex)) {
      return;
    }

    currentTables.forEach((currentTable, currentIndex) => {
      if (matchedCurrent.has(currentIndex)) {
        return;
      }

      if (!isSafeIdentifierRenameMatch(previousTable, currentTable)) {
        return;
      }

      const previousMatches = previousCandidates.get(previousIndex) ?? [];
      previousMatches.push(currentIndex);
      previousCandidates.set(previousIndex, previousMatches);

      const currentMatches = currentCandidates.get(currentIndex) ?? [];
      currentMatches.push(previousIndex);
      currentCandidates.set(currentIndex, currentMatches);
    });
  });

  const pairs: CandidatePair[] = [];
  let lastCurrentIndex = -1;

  previousTables.forEach((_, previousIndex) => {
    const currentMatches = previousCandidates.get(previousIndex) ?? [];
    if (currentMatches.length !== 1) {
      return;
    }

    const currentIndex = currentMatches[0];
    if (currentIndex <= lastCurrentIndex) {
      return;
    }

    const reciprocalMatches = currentCandidates.get(currentIndex) ?? [];
    if (
      reciprocalMatches.length !== 1 ||
      reciprocalMatches[0] !== previousIndex
    ) {
      return;
    }

    pairs.push({ previousIndex, currentIndex });
    lastCurrentIndex = currentIndex;
  });

  return pairs;
};

const isSingleNonIdentifierColumnEdit = (
  previousTable: NormalizedTableBlock,
  currentTable: NormalizedTableBlock
): boolean => {
  const previousHeaders = previousTable.normalizedHeaders;
  const currentHeaders = currentTable.normalizedHeaders;

  if (Math.abs(previousHeaders.length - currentHeaders.length) !== 1) {
    return false;
  }

  const shorterHeaders =
    previousHeaders.length <= currentHeaders.length
      ? previousHeaders
      : currentHeaders;
  const longerHeaders =
    previousHeaders.length > currentHeaders.length
      ? previousHeaders
      : currentHeaders;

  const insertedIndex = findSingleInsertedHeaderIndex(
    shorterHeaders,
    longerHeaders
  );
  if (insertedIndex === null) {
    return false;
  }

  if (isLikelyIdentifierHeader(longerHeaders[insertedIndex])) {
    return false;
  }

  const shorterTable =
    previousHeaders.length <= currentHeaders.length
      ? previousTable
      : currentTable;
  const longerTable =
    previousHeaders.length > currentHeaders.length
      ? previousTable
      : currentTable;

  if (shorterTable.rowTexts.length === 0 || longerTable.rowTexts.length === 0) {
    return true;
  }

  return hasProjectedRowOverlap(shorterTable, longerTable, insertedIndex);
};

const findSingleInsertedHeaderIndex = (
  shorterHeaders: string[],
  longerHeaders: string[]
): number | null => {
  let shorterIndex = 0;
  let insertedIndex: number | null = null;

  for (
    let longerIndex = 0;
    longerIndex < longerHeaders.length;
    longerIndex += 1
  ) {
    if (
      shorterIndex < shorterHeaders.length &&
      shorterHeaders[shorterIndex] === longerHeaders[longerIndex]
    ) {
      shorterIndex += 1;
      continue;
    }

    if (insertedIndex !== null) {
      return null;
    }

    insertedIndex = longerIndex;
  }

  if (shorterIndex !== shorterHeaders.length) {
    return null;
  }

  return insertedIndex;
};

const isSafeIdentifierRenameMatch = (
  previousTable: NormalizedTableBlock,
  currentTable: NormalizedTableBlock
): boolean => {
  if (previousTable.columnCount !== currentTable.columnCount) {
    return false;
  }

  const previousIdentifier = findSingleIdentifierColumn(
    previousTable.headerTexts
  );
  const currentIdentifier = findSingleIdentifierColumn(
    currentTable.headerTexts
  );
  if (!previousIdentifier || !currentIdentifier) {
    return false;
  }

  const previousNonIdentifierHeaders = previousTable.normalizedHeaders.filter(
    (_, index) => index !== previousIdentifier.index
  );
  const currentNonIdentifierHeaders = currentTable.normalizedHeaders.filter(
    (_, index) => index !== currentIdentifier.index
  );

  if (
    previousNonIdentifierHeaders.length !== currentNonIdentifierHeaders.length
  ) {
    return false;
  }

  const sharedNonIdentifierHeaders = countSharedHeaders(
    previousNonIdentifierHeaders,
    currentNonIdentifierHeaders
  );
  const headerOverlap =
    previousNonIdentifierHeaders.length === 0
      ? 0
      : sharedNonIdentifierHeaders / previousNonIdentifierHeaders.length;
  if (headerOverlap < 0.5) {
    return false;
  }

  const previousRowMap = createUniqueRowKeyMap(
    previousTable.rowTexts,
    previousIdentifier.index
  );
  const currentRowMap = createUniqueRowKeyMap(
    currentTable.rowTexts,
    currentIdentifier.index
  );
  if (!previousRowMap || !currentRowMap) {
    return false;
  }

  let sharedKeys = 0;
  previousRowMap.forEach((_, rowKey) => {
    if (currentRowMap.has(rowKey)) {
      sharedKeys += 1;
    }
  });

  return sharedKeys > 0;
};

const countSharedHeaders = (
  previousHeaders: string[],
  currentHeaders: string[]
): number => {
  const previousCounts = new Map<string, number>();
  previousHeaders.forEach((header) => {
    previousCounts.set(header, (previousCounts.get(header) ?? 0) + 1);
  });

  let sharedCount = 0;
  currentHeaders.forEach((header) => {
    const remaining = previousCounts.get(header) ?? 0;
    if (remaining <= 0) {
      return;
    }

    sharedCount += 1;
    previousCounts.set(header, remaining - 1);
  });

  return sharedCount;
};

const normalizeRowKey = (value: string): string => value.trim().toLowerCase();

const hasProjectedRowOverlap = (
  shorterTable: NormalizedTableBlock,
  longerTable: NormalizedTableBlock,
  insertedIndex: number
): boolean => {
  const longerRowCounts = new Map<string, number>();

  longerTable.rowTexts.forEach((row) => {
    const projectedRow = row.filter((_, index) => index !== insertedIndex);
    const signature = createRowSignature(projectedRow);
    longerRowCounts.set(signature, (longerRowCounts.get(signature) ?? 0) + 1);
  });

  let sharedRowCount = 0;
  shorterTable.rowTexts.forEach((row) => {
    const signature = createRowSignature(row);
    const remaining = longerRowCounts.get(signature) ?? 0;
    if (remaining <= 0) {
      return;
    }

    sharedRowCount += 1;
    longerRowCounts.set(signature, remaining - 1);
  });

  return sharedRowCount > 0;
};

const createRowSignature = (row: string[]): string =>
  row.map((cell) => cell.trim()).join("|");

const hasHeaderlessRowOverlap = (
  previousTable: NormalizedTableBlock,
  currentTable: NormalizedTableBlock
): boolean => {
  if (
    previousTable.columnCount === 0 ||
    previousTable.columnCount !== currentTable.columnCount
  ) {
    return false;
  }

  const currentRowSignatures = new Set(
    currentTable.rowTexts.map(createRowSignature)
  );

  if (
    previousTable.rowTexts.some((row) =>
      currentRowSignatures.has(createRowSignature(row))
    )
  ) {
    return true;
  }

  for (let index = 0; index < previousTable.columnCount; index += 1) {
    const previousRowMap = createUniqueRowKeyMap(previousTable.rowTexts, index);
    const currentRowMap = createUniqueRowKeyMap(currentTable.rowTexts, index);
    if (!previousRowMap || !currentRowMap) {
      continue;
    }

    if (
      Array.from(previousRowMap.keys()).some((key) => currentRowMap.has(key))
    ) {
      return true;
    }
  }

  return false;
};

const groupIndicesBySignature = (
  tables: NormalizedTableBlock[]
): Map<string, number[]> => {
  const map = new Map<string, number[]>();
  tables.forEach((table, index) => {
    const indices = map.get(table.headerSignature) ?? [];
    indices.push(index);
    map.set(table.headerSignature, indices);
  });
  return map;
};

const tableObjectToNormalizedTable = (
  table: TableObject
): NormalizedTableBlock =>
  table.table
    ? parseMarkdownTable(table.table)
    : {
        headerCells: table.headerCells.map(createPlainTableCell),
        headerTexts: table.headerCells,
        normalizedHeaders:
          table.normalizedHeaders || table.headerCells.map(normalizeHeaderCell),
        rows: table.rowCells.map((row) => row.map(createPlainTableCell)),
        rowTexts: table.rowCells,
        rawMarkdown: table.table,
        headerSignature: createHeaderSignature(table.headerCells),
        columnCount: table.columnCount || table.headerCells.length,
        isHeaderless: table.isHeaderless,
      };

const createPlainTableCell = (value: string): NormalizedTableCell => ({
  rawMarkdown: value,
  plainText: value,
  normalizedText: value.trim().toLowerCase(),
});

const buildLcsPairs = <TPrevious, TCurrent>(
  previousValues: TPrevious[],
  currentValues: TCurrent[],
  isMatch: (previousValue: TPrevious, currentValue: TCurrent) => boolean,
  toPair: (previousValue: TPrevious, currentValue: TCurrent) => CandidatePair
): CandidatePair[] => {
  const lcs = Array.from({ length: previousValues.length + 1 }, () =>
    Array(currentValues.length + 1).fill(0)
  );

  for (
    let previousIndex = previousValues.length - 1;
    previousIndex >= 0;
    previousIndex -= 1
  ) {
    for (
      let currentIndex = currentValues.length - 1;
      currentIndex >= 0;
      currentIndex -= 1
    ) {
      if (isMatch(previousValues[previousIndex], currentValues[currentIndex])) {
        lcs[previousIndex][currentIndex] =
          lcs[previousIndex + 1][currentIndex + 1] + 1;
      } else {
        lcs[previousIndex][currentIndex] = Math.max(
          lcs[previousIndex + 1][currentIndex],
          lcs[previousIndex][currentIndex + 1]
        );
      }
    }
  }

  const pairs: CandidatePair[] = [];
  let previousIndex = 0;
  let currentIndex = 0;

  while (
    previousIndex < previousValues.length &&
    currentIndex < currentValues.length
  ) {
    if (isMatch(previousValues[previousIndex], currentValues[currentIndex])) {
      pairs.push(
        toPair(previousValues[previousIndex], currentValues[currentIndex])
      );
      previousIndex += 1;
      currentIndex += 1;
      continue;
    }

    if (
      lcs[previousIndex + 1][currentIndex] >=
      lcs[previousIndex][currentIndex + 1]
    ) {
      previousIndex += 1;
    } else {
      currentIndex += 1;
    }
  }

  return pairs;
};
